import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Clock } from "lucide-react";
import type { College } from "@/types/database";
import { useServerTime } from "@/hooks/useServerTime";
import { getServerTimeIST, formatISTDate } from "@/utils/timezoneUtils";
import {
  formatRegistrationDate,
  getRegistrationAvailability,
} from "@/utils/registrationAvailability";
import { RegistrationStateCard } from "@/components/registration/RegistrationStateCard";

interface RegistrationDateGuardProps {
  children: React.ReactNode;
  college: College | null;
  isLoading: boolean;
}

export const RegistrationDateGuard = React.memo(
  ({ children, college, isLoading }: RegistrationDateGuardProps) => {
    const { offset: serverTimeOffset } = useServerTime();
    const [serverDate, setServerDate] = useState<string | null>(null);
    const [isValidatingDate, setIsValidatingDate] = useState(true);

    useEffect(() => {
      const updateServerDate = () => {
        if (serverTimeOffset === null) return;

        try {
          const serverTime = getServerTimeIST(serverTimeOffset);
          const currentDate = formatISTDate(serverTime);
          setServerDate(currentDate);
        } catch (error) {
          console.error("Error calculating server date:", error);
          setServerDate(null);
        } finally {
          setIsValidatingDate(false);
        }
      };

      updateServerDate();
    }, [serverTimeOffset]);

    if (isLoading || isValidatingDate || serverTimeOffset === null) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <Clock className="h-12 w-12 text-blue-500 mx-auto animate-spin" />
                <h3 className="text-lg font-semibold text-blue-600">
                  Loading...
                </h3>
                <p className="text-gray-600">
                  {isValidatingDate || serverTimeOffset === null
                    ? "Validating availability..."
                    : "Checking registration availability..."}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (!serverDate) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <Clock className="h-12 w-12 text-red-500 mx-auto" />
                <h3 className="text-lg font-semibold text-red-600">
                  Time Validation Error
                </h3>
                <p className="text-gray-600">
                  Unable to validate server time. Please try again.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    const availability = getRegistrationAvailability(college, serverDate);

    if (availability.status === "invalid") {
      return (
        <RegistrationStateCard
          title="Registration Not Available"
          description="College drive information is not available at this time."
          note="Please contact your placement office if you believe this link should be active."
          icon="invalid"
          tone="danger"
        />
      );
    }

    if (availability.status === "unscheduled") {
      return (
        <RegistrationStateCard
          title="Registration Schedule Pending"
          description="Registration dates have not been published for this college yet."
          note="Please check with your placement office for the official registration window."
          icon="calendar"
          tone="warning"
        />
      );
    }

    if (availability.status === "upcoming" && availability.startDate) {
      return (
        <RegistrationStateCard
          title="Registration Not Yet Open"
          description={`Registration will open on ${formatRegistrationDate(availability.startDate)}.`}
          note="Please come back on or after the start date in IST."
          icon="calendar"
          tone="warning"
        />
      );
    }

    if (availability.status === "closed" && availability.endDate) {
      return (
        <RegistrationStateCard
          title="Registration Closed"
          description={`Registration ended on ${formatRegistrationDate(availability.endDate)}.`}
          note="Please contact your placement office if you need assistance."
          icon="calendar"
          tone="danger"
        />
      );
    }

    return <>{children}</>;
  }
);

RegistrationDateGuard.displayName = "RegistrationDateGuard";
